"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useStudioEnrollmentsGet } from "@/features/enrollments/hooks/useStudioEnrollmentsGet";
import { useAccessToken, useTenantData } from "@/hooks/shared";
import {
  mergePendingEnrollmentsForReview,
  PENDING_ENROLLMENTS_REVIEW_WIDGET_KEYS,
  parsePendingEnrollmentsReviewFromWidgetData,
  pendingEnrollmentIdsNeedingReviewLinks,
} from "@/lib/dashboard/pending-enrollments-review";
import { fetchWidgetDataBatched } from "@/lib/dashboard/widgets-client";
import { QueryKeys } from "@/lib/types";
import { fetchEnrollmentById } from "@/features/enrollments/shared/fetchEnrollmentById";
import type { GetEnrollmentResponse } from "@/openapi/generated";
import { enrollmentByIdQueryKey } from "./useEnrollmentById";

export const pendingEnrollmentsReviewQueryKey = (
  tenantId: number | null | undefined,
) => [QueryKeys.WidgetData, tenantId, "pending-enrollments-review"] as const;

export { enrollmentByIdQueryKey };

export function usePendingEnrollmentsReview({
  highlightEnrollmentId = null,
}: {
  highlightEnrollmentId?: number | null;
} = {}) {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const studioQuery = useStudioEnrollmentsGet();

  const widgetQuery = useQuery({
    queryKey: pendingEnrollmentsReviewQueryKey(tenantId),
    enabled: !!accessToken && tenantId != null && tenantId > 0,
    queryFn: async () => {
      const token = accessToken;
      const tid = tenantId;
      if (!token || tid == null) {
        throw new Error("Missing session or tenant for pending enrollments");
      }
      const items = await fetchWidgetDataBatched(token, tid, [
        ...PENDING_ENROLLMENTS_REVIEW_WIDGET_KEYS,
      ]);
      return parsePendingEnrollmentsReviewFromWidgetData(items);
    },
    staleTime: 60_000,
    refetchInterval: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const highlightId =
    highlightEnrollmentId != null && highlightEnrollmentId > 0
      ? highlightEnrollmentId
      : null;

  const byIdQuery = useQuery({
    queryKey: enrollmentByIdQueryKey(highlightId ?? 0),
    enabled: !!accessToken && highlightId != null,
    queryFn: async () => {
      const token = accessToken;
      if (!token || highlightId == null) return null;
      return fetchEnrollmentById(token, tenantId, highlightId);
    },
    staleTime: 60_000,
    refetchInterval: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const basePendingEnrollments = useMemo(() => {
    return mergePendingEnrollmentsForReview([
      studioQuery.data?.items ?? [],
      widgetQuery.data ?? [],
      byIdQuery.data ? [byIdQuery.data] : [],
    ]);
  }, [studioQuery.data?.items, widgetQuery.data, byIdQuery.data]);

  const idsNeedingLinks = useMemo(
    () => pendingEnrollmentIdsNeedingReviewLinks(basePendingEnrollments),
    [basePendingEnrollments],
  );
  const idsNeedingLinksKey = idsNeedingLinks.join(",");

  const detailsQuery = useQuery({
    queryKey: [
      QueryKeys.ClassesEnrollmentsByStudentId,
      "pending-review-details",
      idsNeedingLinksKey,
    ],
    enabled: !!accessToken && idsNeedingLinks.length > 0,
    queryFn: async () => {
      const token = accessToken;
      if (!token) return [];
      const rows = await Promise.all(
        idsNeedingLinks.map((id) => fetchEnrollmentById(token, tenantId, id)),
      );
      return rows.filter((row): row is GetEnrollmentResponse => row != null);
    },
    staleTime: 60_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });

  const pendingEnrollments = useMemo(() => {
    return mergePendingEnrollmentsForReview([
      basePendingEnrollments,
      detailsQuery.data ?? [],
    ]);
  }, [basePendingEnrollments, detailsQuery.data]);

  const isPending =
    widgetQuery.isLoading ||
    (idsNeedingLinks.length > 0 && detailsQuery.isLoading) ||
    (highlightId != null &&
      byIdQuery.isLoading &&
      !pendingEnrollments.some(
        (enrollment) => Number(enrollment.enrollmentId) === highlightId,
      ));

  const isFetching =
    widgetQuery.isFetching ||
    studioQuery.isFetching ||
    byIdQuery.isFetching ||
    detailsQuery.isFetching;

  const refetch = async () => {
    await Promise.all([
      widgetQuery.refetch(),
      studioQuery.refetch(),
      highlightId != null ? byIdQuery.refetch() : Promise.resolve(),
      idsNeedingLinks.length > 0 ? detailsQuery.refetch() : Promise.resolve(),
    ]);
  };

  return {
    pendingEnrollments,
    isPending,
    isFetching,
    refetch,
  };
}
